// Lesson 1: Menyebutkan Nama dan Sapaan - Question Configurations

export interface QuestionConfig {
  type: string;
  wordBank: number[]; // Word IDs to use
  questionSentence?: number[]; // Order of word IDs for sentence questions
  correctAnswer?: number[] | number; // Correct answer(s)
  options?: Array<{ id: number; text?: string; image?: string }>; // Multiple choice options
  imagePrompt?: string; // For image-based questions
}

export interface MatchingPair {
  indonesian: string;
  batak: string;
}

// 1. Vocabulary Introduction Config (Image + Word)
export const unit1Lesson1VocabIntroConfigs = [
  {
    type: "vocab-intro",
    introWordId: 101, // "Anakkon" (Nama)
    imagePrompt: "name_tag",
  },
  {
    type: "vocab-intro",
    introWordId: 102, // "Siapa" (Siapa)
    imagePrompt: "question_mark",
  },
  {
    type: "vocab-intro",
    introWordId: 103, // "Saha" (Siapa)
    imagePrompt: "question_mark",
  },
  {
    type: "vocab-intro",
    introWordId: 104, // "Aha" (Siapa)
    imagePrompt: "question_mark",
  },
  {
    type: "vocab-intro",
    introWordId: 105, // "Au" (Saya)
    imagePrompt: "person_pointing_self",
  },
  {
    type: "vocab-intro",
    introWordId: 106, // "Ho" (Kamu)
    imagePrompt: "person_pointing_other",
  },
  {
    type: "vocab-intro",
    introWordId: 107, // "Ia" (Dia)
    imagePrompt: "third_person",
  },
  {
    type: "vocab-intro",
    introWordId: 108, // "Horas" (Salam)
    imagePrompt: "greeting_wave",
  },
  {
    type: "vocab-intro",
    introWordId: 109, // "Si" (penanda nama)
    imagePrompt: "name_prefix",
  },
];

// 3. Fill in the Blank Questions
export const unit1Lesson1FillInBlankConfigs: QuestionConfig[] = [
    {
        type: "fill-in-the-blank",
        wordBank: [101, 102, 103, 104, 105, 106, 107, 111, 112, 999],
        questionSentence: [999, 111, 112, 200], // "_____ do anakkonmu?"
        correctAnswer: 104, // "Aha"
        options: [
            { id: 104, text: "Aha" }, // CORRECT - question word
            { id: 108, text: "Horas" }, // greeting, not question
            { id: 109, text: "Si" }, // name prefix, not question
            { id: 101, text: "Anakkon" }  // noun, not question word
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [101, 105, 106, 107, 109, 110, 999],
        questionSentence: [101, 999, 109, 110], // "Anakkon _____ si Rinto"
        correctAnswer: 105, // "Au"
        options: [
            { id: 105, text: "Au" }, // CORRECT - "My name is Rinto"
            { id: 108, text: "Horas" }, // greeting, not pronoun
            { id: 109, text: "Si" }, // name prefix, already used
            { id: 101, text: "Anakkon" }  // noun, already used
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [105, 106, 107, 108, 999],
        questionSentence: [108, 999, 110], // "Horas _____ Rinto!"
        correctAnswer: 106, // "Ho"
        options: [
            { id: 106, text: "Ho" }, // CORRECT - "Hello, you Rinto!" (greeting someone)
            { id: 108, text: "Horas" }, // greeting word, already used
            { id: 101, text: "Anakkon" }, // noun, doesn't fit context
            { id: 109, text: "Si" }  // name prefix, wrong position
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [101, 102, 103, 107, 999],
        questionSentence: [999, 107, 200], // "_____ ia?"
        correctAnswer: 102, // "Siapa"
        options: [
            { id: 102, text: "Siapa" }, // CORRECT - "Who is he/she?"
            { id: 108, text: "Horas" }, // greeting, not question
            { id: 107, text: "Ia" }, // pronoun, already used
            { id: 109, text: "Si" }  // name prefix, not question word
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [105, 106, 107, 109, 110, 999],
        questionSentence: [999, 109, 113], // "_____ si Rinto"
        correctAnswer: 107, // "Ia"
        options: [
            { id: 107, text: "Ia" }, // CORRECT - "He/She is Rinto"
            { id: 108, text: "Horas" }, // greeting, not pronoun
            { id: 102, text: "Siapa" }, // question word, not statement
            { id: 101, text: "Anakkon" }  // noun, doesn't fit
        ]
    }
];

// 5. Compose Sentences (Rearrange words)
export const unit1Lesson1ComposeSentenceConfigs: QuestionConfig[] = [
    {
        type: "compose-sentences",
        wordBank: [101, 105, 109, 110, 106, 107], // Word options to choose from
        questionSentence: [101, 105, 109, 110], // Correct order: "Anakkon au Rinto"
        correctAnswer: [101, 105, 110]
    },
    {
        type: "compose-sentences",
        wordBank: [104, 111, 112, 105, 106, 107],
        questionSentence: [104, 111, 112], // "Aha do anakkonmu"
        correctAnswer: [104, 112]
    },
    {
        type: "compose-sentences",
        wordBank: [108, 105, 106, 107, 110],
        questionSentence: [108, 110], // "Horas Rinto"
        correctAnswer: [108, 110]
    },
    {
        type: "compose-sentences",
        wordBank: [101, 107, 109, 110, 105, 106, 113],
        questionSentence: [101, 107, 109, 113], // "Anakkon ia si Kaido"
        correctAnswer: [101, 107, 113]
    },
    {
        type: "compose-sentences",
        wordBank: [102, 101, 106, 105, 107, 111],
        questionSentence: [102, 101, 106], // "Siapa anakkon ho"
        correctAnswer: [102, 101, 106]
    }
];

// 7. Word Matching (Indonesian ↔ Batak)
export const unit1Lesson1WordMatchingPairs: MatchingPair[] = [
  { indonesian: "Salam/Halo", batak: "ᯂᯬᯒᯘ᯲" }, // Horas
  { indonesian: "Nama", batak: "ᯀᯉᯃᯒᯉ᯲" }, // Anakkon
  { indonesian: "Saya", batak: "ᯀᯮ" }, // Au
  { indonesian: "Kamu", batak: "ᯂᯬ" }, // Ho
  { indonesian: "Dia", batak: "ᯤᯀ" }, // Ia
  { indonesian: "Siapa", batak: "ᯘᯍ" }, // Saha
];

// Matching words configuration
export const unit1Lesson1MatchingWordsConfig = {
  type: "matching-words",
  pairs: unit1Lesson1WordMatchingPairs,
};

// 8. Word Translation (Single word quiz)
export const unit1Lesson1WordTranslationConfigs: QuestionConfig[] = [
  {
    type: "word-translation",
    wordBank: [108],
    correctAnswer: 108, // Horas
    options: [
      { id: 108, text: "Salam/Halo", image: "./assets/illustration/illustration-salam.png" },
      { id: 101, text: "Nama", image: "./assets/illustration/illustration-nama.png" },
      { id: 105, text: "Saya", image: "./assets/illustration/illustration-saya.png" },
      { id: 106, text: "Kamu", image: "./assets/illustration/illustration-kamu.png" },
    ],
  },
  { 
    type: "word-translation",
    wordBank: [105],
    correctAnswer: 105, // Au
    options: [
      { id: 105, text: "Saya", image: "./assets/illustration/illustration-saya.png" },
      { id: 106, text: "Kamu", image: "./assets/illustration/illustration-kamu.png"},
      { id: 107, text: "Dia", image: "./assets/illustration/illustration-dia.png" },
      { id: 102, text: "Siapa", image: "./assets/illustration/illustration-siapa.png" },
    ],
  },
  {
    type: "word-translation",
    wordBank: [106],
    correctAnswer: 106, // Ho
    options: [
      { id: 106, text: "Kamu", image: "./assets/illustration/illustration-kamu.png" },
      { id: 105, text: "Saya", image: "./assets/illustration/illustration-saya.png"  },
      { id: 107, text: "Dia", image: "./assets/illustration/illustration-dia.png" },
      { id: 101, text: "Nama", image: "./assets/illustration/illustration-nama.png"  },
    ],
  },
  {
    type: "word-translation",
    wordBank: [107],
    correctAnswer: 107, // Ia
    options: [
      { id: 107, text: "Dia", image: "./assets/illustration/illustration-dia.png" },
      { id: 105, text: "Saya", image: "./assets/illustration/illustration-saya.png" },
      { id: 106, text: "Kamu", image: "./assets/illustration/illustration-kamu.png"  },
      { id: 108, text: "Salam/Halo", image: "./assets/illustration/illustration-salam.png" },
    ],
  },
  {
    type: "word-translation",
    wordBank: [101],
    correctAnswer: 101, // Anakkon
    options: [
      { id: 101, text: "Nama", image: "./assets/illustration/illustration-nama.png"  },
      { id: 102, text: "Siapa", image: "./assets/illustration/illustration-siapa.png" },
      { id: 103, text: "Saha (Siapa)", image: "./assets/illustration/illustration-siapa.png" },
      { id: 111, text: "do (penghubung)", image: "./assets/illustration/illustration-penghubung.png" },
    ],
  },
];

// Complete lesson configuration (only those with existing components)
export const unit1Lesson1QuestionTypes = [
  "vocab-intro",
  "fill-in-the-blank",
  "compose-sentences",
  "matching-words",
  "word-translation",
];
